import { useEffect, useCallback, useState, useMemo, useRef } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';
import Lottie from 'react-lottie';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives/responsive';
import { ConfluenceBackground, JiraBackground } from '@atlassian/product-wireframes';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { getIdentityBaseUrl } from '@/components/signup/utils/environment';
import { getFirstName } from '@/components/signup/utils/get-first-name';
import { UfoCustomTimingWrapper } from '@/components/signup/utils/ufo-custom-timing-wrapper';
import {
	ufoEventRedirectToProduct,
	ufoEventOnboardingWizardProjectCreation,
	ufoEventOnboardingWizardProjectCreationIncludingProvisioning,
} from '@/components/signup/utils/ufo-events';
import { UfoCustomTimingKey } from '@/components/signup/utils/ufo.types';
import { useUserContext } from '@/components/user-context/user-context-provider';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { WizardCreateProjectPayload } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { DwellTimeWrapper } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/components/dwell-time-wrapper/dwell-time-wrapper';
import { useCloudStatus } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-cloud-status';
import { useCloudStatusRedirection } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-cloud-status-redirection';
import { getProductQueryParamTeamType } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/models/user-segmentation';
import { trackPerceivedProvisioningEvent } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/utils/analytics';
import { useWizardConfiguration } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/controllers';
import { type ProjectConfig } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/utils/types';
import { PROJECT_CONFIG_SESSION_STORAGE_KEY } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/page-switcher/constants';

import { useLoggedInUser } from '../../../../../../../../../utils/logged-in-user-provider';
import ProgressBar from '../../../sections/progress-bar';
import { getTopLevelDomain } from '../../../utils/create-product-url';
import { getGenericSampleVersion } from '../../setup/jira/utils';

import IconsAnimationLight from './lotties/icons.light.json';
import EndProvisioningAnimation from './lotties/provisioning-end-animation.json';
import ProvisioningAnimationLight from './lotties/provisioning.light.json';
import { ModalOverlay } from './modal-overlay/modal-overlay';

const EndAnimationContainerStyle = css({
	width: 512,
});

const EndAnimationParagraphStyle = css({
	margin: '16px',
	textAlign: 'center',
	fontWeight: '400',
	lineHeight: '24px',
});

const modalContentStyle = css({
	display: 'flex',
	flexFlow: 'column',
	alignItems: 'center',
	alignSelf: 'center',
	position: 'relative',
	zIndex: 1,
});

const backgroundAnimationStyle = css({
	display: 'none',
	[media.above.md]: {
		display: 'block',
		position: 'absolute',
		insetBlockStart: 0,
		insetInlineStart: 0,
		width: '100%',
		height: '100%',
		zIndex: 0,
		borderRadius: '4px',
		pointerEvents: 'none',
		overflow: 'hidden',
	},
});

const iconAnimationStyle = css({
	maxWidth: '100%',
	width: '400px',
	height: '180px',
});

const EndAnimationHeaderStyle = css({
	textAlign: 'center',
	fontSize: '24px',
	fontWeight: 653,
	lineHeight: '28px',
	marginBlockStart: 160,
	marginBlockEnd: 0,
});

const ROVO_POLLING_MAX_RETRIES = 20;

export const ControlAnimation = ({
	provisioningCompleteDefault = false,
}: {
	provisioningCompleteDefault?: boolean;
	// eslint-disable-next-line complexity
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const {
		profile: { cohorts },
	} = useUserContext();
	const [provisioningComplete, setProvisioningComplete] = useState(provisioningCompleteDefault);
	const userInfo = useLoggedInUser();
	const name = getFirstName(userInfo.name);

	const {
		isProduction,
		onDemandUrl,
		createCloudStatus,
		cloudId,
		perceivedProvisioningStartTime,
		productActivationStatusResponses,
	} = useCloudStatus();

	const { redirectUrl, deferRedirectView, setDeferRedirectView, resolveBlockingRedirectPromises } =
		useCloudStatusRedirection();

	const { products, signupId, aiLedOnboarding, edition, loomIntent } = useSearchParameters();
	const productKey = products.length > 0 ? products[0] : undefined;
	const isJiraAiLedOnboarding = aiLedOnboarding === 'jira';
	const isConfluenceAiLedOnboarding = !!aiLedOnboarding && aiLedOnboarding !== 'jira';
	const shouldCreateJiraProject = isJiraAiLedOnboarding;
	const Background = isConfluenceAiLedOnboarding ? ConfluenceBackground : JiraBackground;
	const isBusinessDomain = cohorts?.domainType === 'business-domain';
	const isPaidEdition = edition === 'standard' || edition === 'premium';

	// Jira project creation hooks and state
	const stargateClient = useBxpWebStargateClient();
	const {
		teamType,
		wizardConfiguration,
		projectDetails: wizardProjectDetails,
	} = useWizardConfiguration();
	const isCreateRequestInProgress = useRef(false);
	const isRovoPollingInProgress = useRef(false);
	const rovoPollingTimer = useRef<ReturnType<typeof setTimeout>>();
	const [hasCreatedProject, setHasCreatedProject] = useState(false);
	const [hasCompletedRovoPolling, setHasCompletedRovoPolling] = useState(false);
	const [rovoPollingAttempts, setRovoPollingAttempts] = useState(0);
	const [aiLedOnboardingUrl, setAiLedOnboardingUrl] = useState<string | undefined>(undefined);
	const projectDetails = wizardProjectDetails;
	const isProgressBarSuccessPreconditionsMet =
		(!shouldCreateJiraProject || hasCreatedProject) &&
		(!isConfluenceAiLedOnboarding || hasCompletedRovoPolling);

	const aifcOnboardingUrl = useMemo(() => {
		if (!onDemandUrl || !aiLedOnboarding) {
			return undefined;
		}

		const instanceUrl =
			'https://' + encodeURIComponent(String(onDemandUrl)) + getTopLevelDomain(isProduction);

		return `${instanceUrl}/wiki/welcome?twcAifcContentType=${aiLedOnboarding}&twcTeamType=${teamType}`;
	}, [onDemandUrl, aiLedOnboarding, isProduction, teamType]);

	// Loom Land opt-in (STARB-4196 / TWCG-2108): a collection provision that
	// originated from the Loom funnel carries the internal `loomIntent=true` wac
	// param. Route the user into Loom's welcome onboarding flow (`skipUpgrade`
	// keeps them in onboarding). `loomIntent` is an internal wac param and is not
	// forwarded onto the Loom URL.
	const loomOnboardingUrl = useMemo(() => {
		if (loomIntent !== 'true' || !cloudId) {
			return undefined;
		}

		const loomWelcomeHost = isProduction ? 'https://www.loom.com' : 'https://stage.loom.com';

		return `${loomWelcomeHost}/welcome?siteId=${encodeURIComponent(cloudId)}&skipUpgrade`;
	}, [loomIntent, cloudId, isProduction]);

	const boardViewUrl = useCallback(
		(projectId: number) => {
			if (!onDemandUrl || !isJiraAiLedOnboarding) {
				return undefined;
			}

			const boardId = projectId - 10000 + 1;
			const instanceUrl =
				'https://' + encodeURIComponent(String(onDemandUrl)) + getTopLevelDomain(isProduction);

			return `${instanceUrl}/jira/software/projects/${projectDetails.projectKey}/boards/${boardId}`;
		},
		[onDemandUrl, isJiraAiLedOnboarding, isProduction, projectDetails.projectKey],
	);

	const redirectCallback = useCallback(
		async (url: string) => {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'succeeded',
				actionSubject: 'provisioning',
				actionSubjectId: 'twcProvisioningScreen',
				source: '/ondemand/signup/confirmation',
			}).fire();

			createAnalyticsEvent({
				eventName: 'redirectedToProduct',
				eventComponent: 'confirmationPage',
				event: 'redirected',
				eventType: 'ui',
				action: 'redirected',
			}).fire();

			trackPerceivedProvisioningEvent({
				createAnalyticsEvent,
				productKey,
				finalProvisioningScreen: 'twc-provisioning-screen',
				startTimeSeconds: perceivedProvisioningStartTime,
				signupId,
			});

			// SLO "good event" for redirect into product
			ufoEventRedirectToProduct.success({
				customData: {
					reactComponent: 'TwcProvisioningScreen',
					analyticsEvent: 'redirected',
					redirectFrom: 'confirmationPage',
					finalProvisioningScreen: 'twc-provisioning-screen',
				},
			});

			await resolveBlockingRedirectPromises();

			window.location.assign(url);
		},
		[
			createAnalyticsEvent,
			perceivedProvisioningStartTime,
			productKey,
			signupId,
			resolveBlockingRedirectPromises,
		],
	);

	const projectConfig = useMemo((): ProjectConfig => {
		// Compute default config as fallback
		const defaultIssueTypes = wizardConfiguration.issueTypes;
		const defaultColumns = wizardConfiguration.columns.map((columnMessage) =>
			formatMessage({
				id: columnMessage.id,
				defaultMessage: columnMessage.defaultMessage,
			}),
		);

		const selectedIssueTypes = defaultIssueTypes.filter((issue) => issue.isPreSelected);

		const defaultProjectConfig = {
			projectName: formatMessage(projectDetails.defaultProjectName),
			issueTypes: selectedIssueTypes.map(({ icon, avatarId }) => ({
				name: icon,
				avatarId,
			})),
			columns: defaultColumns,
		};

		const storedValue = sessionStorage.getItem(PROJECT_CONFIG_SESSION_STORAGE_KEY);

		if (storedValue) {
			try {
				return JSON.parse(storedValue) as ProjectConfig;
			} catch {
				return defaultProjectConfig;
			}
		}

		return defaultProjectConfig;
	}, [wizardConfiguration, projectDetails.defaultProjectName, formatMessage]);

	const createJiraProject = useCallback(async () => {
		if (!cloudId || !shouldCreateJiraProject || hasCreatedProject || !projectConfig) {
			return;
		}

		ufoEventOnboardingWizardProjectCreation.start();
		ufoEventOnboardingWizardProjectCreation.setCustomData({ signupId });

		ufoEventOnboardingWizardProjectCreationIncludingProvisioning.startSubstep(
			UfoCustomTimingKey.ONBOARDING_WIZARD_PROJECT_CREATE_API_CALL,
		);

		try {
			isCreateRequestInProgress.current = true;

			const sampleDataVersion =
				projectDetails.template === 'KANBAN' || projectDetails.template === 'SCRUM'
					? getGenericSampleVersion(teamType, projectDetails.template)
					: undefined;

			const projectPayload: WizardCreateProjectPayload = {
				projectName: projectConfig.projectName,
				issueTypes: projectConfig.issueTypes,
				statusNames: projectConfig.columns,
				projectKey: projectDetails.projectKey,
				template: projectDetails.template,
				cloudId: cloudId,
				teamType: getProductQueryParamTeamType(teamType),
				customDetail: {
					enableExtraFields: true,
					...(sampleDataVersion ? { sampleDataVersion } : {}),
				},
			};

			const { taskId } = await stargateClient.wizardProjectCreate(projectPayload);

			// Poll for project creation to complete
			const { status, result } = await stargateClient.pollTaskUntilComplete(taskId, cloudId);

			if (status === 'COMPLETE') {
				setHasCreatedProject(true);
				setAiLedOnboardingUrl(boardViewUrl(Number(result.projectId)));

				ufoEventOnboardingWizardProjectCreation.success({
					customData: {
						template: projectDetails.template,
					},
				});

				createAnalyticsEvent({
					eventType: 'track',
					action: 'succeeded',
					actionSubject: 'jiraProjectCreation',
					actionSubjectId: 'twcProvisioningScreen',
					source: '/ondemand/signup/confirmation',
					attributes: {
						template: projectDetails.template,
						teamType: teamType,
					},
				}).fire();
			}
		} catch (error) {
			ufoEventOnboardingWizardProjectCreation.failure({
				customData: {
					template: projectDetails.template,
				},
				error,
			});

			createAnalyticsEvent({
				eventType: 'track',
				action: 'failed',
				actionSubject: 'jiraProjectCreation',
				actionSubjectId: 'twcProvisioningScreen',
				source: '/ondemand/signup/confirmation',
				attributes: {
					template: projectDetails.template,
					teamType: teamType,
					error: error instanceof Error ? error.message : 'Unknown error',
				},
			}).fire();

			// Still mark as created to prevent retries
			setHasCreatedProject(true);
		} finally {
			isCreateRequestInProgress.current = false;
		}
	}, [
		cloudId,
		shouldCreateJiraProject,
		hasCreatedProject,
		signupId,
		projectConfig,
		projectDetails.projectKey,
		projectDetails.template,
		teamType,
		stargateClient,
		createAnalyticsEvent,
		boardViewUrl,
	]);

	const callbackForRovoCheck = useCallback(async () => {
		const scheduleNextPollingAttempt = () => {
			rovoPollingTimer.current = setTimeout(() => {
				isRovoPollingInProgress.current = false;
				setRovoPollingAttempts((previousAttempts) => previousAttempts + 1);
			}, 3000);
		};

		isRovoPollingInProgress.current = true;

		if (
			isBusinessDomain &&
			isPaidEdition &&
			cloudId &&
			rovoPollingAttempts < ROVO_POLLING_MAX_RETRIES
		) {
			try {
				const { products } = await stargateClient.getLicenceInformationStatus({
					cloudId,
				});
				const rovoData = products['rovo.ondemand'];

				if (rovoData?.state === 'ACTIVE') {
					createAnalyticsEvent({
						eventType: 'track',
						action: 'succeeded',
						actionSubject: 'rovoPolling',
						actionSubjectId: 'twcProvisioningScreen',
						source: '/ondemand/signup/confirmation',
						attributes: {
							attempts: rovoPollingAttempts,
						},
					}).fire();

					setHasCompletedRovoPolling(true);
					isRovoPollingInProgress.current = false;
					return;
				}
			} catch {
				scheduleNextPollingAttempt();
				return;
			}

			scheduleNextPollingAttempt();
		} else if (rovoPollingAttempts === ROVO_POLLING_MAX_RETRIES) {
			createAnalyticsEvent({
				eventType: 'track',
				action: 'maxRetriesAttempted',
				actionSubject: 'rovoPolling',
				actionSubjectId: 'twcProvisioningScreen',
				source: '/ondemand/signup/confirmation',
			}).fire();

			setHasCompletedRovoPolling(true);
			isRovoPollingInProgress.current = false;
		} else {
			createAnalyticsEvent({
				eventType: 'track',
				action: 'notEligible',
				actionSubject: 'rovoPolling',
				actionSubjectId: 'twcProvisioningScreen',
				source: '/ondemand/signup/confirmation',
				attributes: {
					domainType: cohorts?.domainType,
					edition,
					hasCloudId: !!cloudId,
				},
			}).fire();

			setHasCompletedRovoPolling(true);
			isRovoPollingInProgress.current = false;
		}
	}, [
		cloudId,
		cohorts?.domainType,
		createAnalyticsEvent,
		edition,
		isBusinessDomain,
		isPaidEdition,
		rovoPollingAttempts,
		stargateClient,
	]);

	const getOnboardingUrl = useCallback(() => {
		if (loomOnboardingUrl) {
			return loomOnboardingUrl;
		}

		if (isConfluenceAiLedOnboarding && aifcOnboardingUrl) {
			return aifcOnboardingUrl;
		}

		if (isJiraAiLedOnboarding && aiLedOnboardingUrl) {
			return aiLedOnboardingUrl;
		}

		return redirectUrl;
	}, [
		loomOnboardingUrl,
		isConfluenceAiLedOnboarding,
		aifcOnboardingUrl,
		isJiraAiLedOnboarding,
		aiLedOnboardingUrl,
		redirectUrl,
	]);

	useEffect(() => {
		if (
			createCloudStatus === 'success' &&
			productActivationStatusResponses &&
			productActivationStatusResponses.length > 0
		) {
			// Wait for Jira project creation before redirecting when this screen owns project creation.
			if (shouldCreateJiraProject && !hasCreatedProject) {
				return;
			}

			// Wait for Rovo polling before redirecting
			if (isConfluenceAiLedOnboarding && !hasCompletedRovoPolling) {
				return;
			}

			setProvisioningComplete(true);

			setTimeout(() => {
				const url = `${getIdentityBaseUrl()}/login?${new URLSearchParams({
					continue: getOnboardingUrl(),
					application: 'wac--teamwork-collection',
				})}`;

				redirectCallback(url);
			}, 3500); // give time for final animation to play
		}
	}, [
		createCloudStatus,
		redirectCallback,
		productActivationStatusResponses,
		shouldCreateJiraProject,
		hasCreatedProject,
		isConfluenceAiLedOnboarding,
		hasCompletedRovoPolling,
		getOnboardingUrl,
	]);

	// Trigger Jira project creation when provisioning completes
	useEffect(() => {
		if (
			projectConfig &&
			createCloudStatus === 'success' &&
			cloudId &&
			shouldCreateJiraProject &&
			!hasCreatedProject &&
			!isCreateRequestInProgress.current
		) {
			createJiraProject();
		}
	}, [
		projectConfig,
		createCloudStatus,
		cloudId,
		shouldCreateJiraProject,
		hasCreatedProject,
		createJiraProject,
	]);

	// Trigger Rovo polling when Confluence AI-led provisioning completes.
	useEffect(() => {
		if (
			createCloudStatus === 'success' &&
			isConfluenceAiLedOnboarding &&
			rovoPollingAttempts <= ROVO_POLLING_MAX_RETRIES &&
			!hasCompletedRovoPolling &&
			!isRovoPollingInProgress.current
		) {
			callbackForRovoCheck();
		}
	}, [
		cloudId,
		createCloudStatus,
		hasCompletedRovoPolling,
		isConfluenceAiLedOnboarding,
		callbackForRovoCheck,
		rovoPollingAttempts,
	]);

	useEffect(() => {
		return () => {
			if (rovoPollingTimer.current) {
				clearTimeout(rovoPollingTimer.current);
			}
		};
	}, []);

	// Prevent use-cloud-status.tsx from immediately redirecting when provisioning is finished.
	useEffect(() => {
		if (!deferRedirectView) {
			if (setDeferRedirectView) {
				setDeferRedirectView('twc-provisioning-screen');
			}
		}
	});

	// Initial analytics events — fire on mount
	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: 'twcProvisioningScreen',
		}).fire();
	}, [createAnalyticsEvent]);

	return (
		<UfoCustomTimingWrapper
			ufoEvent={ufoEventRedirectToProduct}
			ufoCustomTimingKey={UfoCustomTimingKey.TWC_PROVISIONING_DWELL_TIME}
		>
			<DwellTimeWrapper
				eventData={{
					action: 'dwelled',
					actionSubject: 'screen',
					actionSubjectId: 'twcProvisioningScreen',
					source: '/ondemand/signup/confirmation',
				}}
				initialPollInterval={5000}
				idleTimeout={120000}
				nextPollIntervalCallback={(x) => x + 5000}
			>
				<Background>
					<ModalOverlay>
						<div css={backgroundAnimationStyle}>
							{provisioningComplete ? (
								<Lottie
									options={{
										loop: false,
										animationData: EndProvisioningAnimation,
									}}
								/>
							) : (
								<Lottie
									options={{
										loop: false,
										animationData: ProvisioningAnimationLight,
									}}
								/>
							)}
						</div>
						<div css={modalContentStyle}>
							{!provisioningComplete ? (
								<>
									<div css={iconAnimationStyle}>
										<Lottie
											options={{
												loop: true,
												animationData: IconsAnimationLight,
											}}
										/>
									</div>
									<ProgressBar isSuccessPreconditionsMet={isProgressBarSuccessPreconditionsMet} />
									<p
										css={EndAnimationParagraphStyle}
										data-testid="teamwork-collection-provisioning-message"
									>
										<FormattedMessage
											id="WacConfirmationPage.teamworkCollectionProvisioning"
											defaultMessage="Getting your collection ready—this will only take a moment. You will be redirected shortly."
										/>
									</p>
								</>
							) : (
								<div css={EndAnimationContainerStyle}>
									<h3 css={EndAnimationHeaderStyle}>
										<h3>
											<FormattedMessage
												id="WacConfirmationPage.teamworkCollectionProvisioningComplete.name"
												defaultMessage="Hi {name},"
												values={{ name }}
											/>
											<br />
											<FormattedMessage
												id="WacConfirmationPage.teamworkCollectionProvisioningComplete.welcome"
												defaultMessage="Welcome to Teamwork Collection"
											/>
										</h3>
									</h3>
									<p
										css={EndAnimationParagraphStyle}
										data-testid="teamwork-collection-provisioning-message-complete"
									>
										<FormattedMessage
											id="WacConfirmationPage.teamworkCollectionProvisioningComplete"
											defaultMessage="You've unlocked a smarter way for your team to connect,{br}collaborate and get more done."
											values={{ br: <br /> }}
										/>
									</p>
								</div>
							)}
						</div>
					</ModalOverlay>
				</Background>
			</DwellTimeWrapper>
		</UfoCustomTimingWrapper>
	);
};
